import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  LoaderCircle,
  LockKeyhole,
  Plus,
  Search,
  X,
} from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import type {
  ReasonMyTaskSort,
  ReasonMyTaskSortOrder,
  ReasonMyTasksSummary,
  ReasonTaskSummary,
  ReasonTaskViewStatus,
} from '../types';
import { getReasonTaskViewStatus } from '../types';

interface ReasonMyTasksProps {
  tasks: ReasonTaskSummary[];
  summary: ReasonMyTasksSummary;
  filter: ReasonTaskViewStatus;
  query: string;
  sort: ReasonMyTaskSort;
  order: ReasonMyTaskSortOrder;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  claiming: boolean;
  nowMs?: number;
  onFilterChange: (filter: ReasonTaskViewStatus) => void;
  onSearch: (query: string) => void;
  onSortChange: (sort: ReasonMyTaskSort, order: ReasonMyTaskSortOrder) => void;
  onLoadMore: () => void;
  onClaim: () => void;
  onOpen: (episodeId: string) => void;
}

const filters: Array<{
  value: ReasonTaskViewStatus;
  label: string;
  icon: typeof Clock3;
}> = [
  { value: 'draft', label: 'Draft', icon: Clock3 },
  { value: 'editable', label: 'Editable', icon: CheckCircle2 },
  { value: 'read_only', label: 'Read-only', icon: LockKeyhole },
];

const sortOptions: Array<{
  label: string;
  sort: ReasonMyTaskSort;
  order: ReasonMyTaskSortOrder;
}> = [
  { label: 'Recently assigned', sort: 'assigned_at', order: 'desc' },
  { label: 'Recently committed', sort: 'committed_at', order: 'desc' },
  { label: 'Edit deadline', sort: 'edit_until', order: 'asc' },
  { label: 'Fail duration: high to low', sort: 'output_duration', order: 'desc' },
  { label: 'Fail duration: low to high', sort: 'output_duration', order: 'asc' },
  { label: 'Episode ID', sort: 'episode_id', order: 'asc' },
];

function formatDate(value: number | null): string {
  if (value == null) return '—';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(value);
}

function formatDuration(value: number): string {
  const seconds = Math.max(0, Math.round(value));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${remainder}s`;
  return `${remainder}s`;
}

export function ReasonMyTasks({
  tasks,
  summary,
  filter,
  query,
  sort,
  order,
  loading,
  loadingMore,
  hasMore,
  claiming,
  nowMs = Date.now(),
  onFilterChange,
  onSearch,
  onSortChange,
  onLoadMore,
  onClaim,
  onOpen,
}: ReasonMyTasksProps) {
  const [searchValue, setSearchValue] = useState(query);
  const visibleTasks = tasks.filter((task) => getReasonTaskViewStatus(task, nowMs) === filter);

  useEffect(() => setSearchValue(query), [query]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSearch(searchValue);
  };

  return (
    <main className="mx-auto w-full max-w-[1400px] px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-brand-text-primary">My Tasks</h1>
          <p className="mt-1 text-sm text-brand-text-secondary">
            Permanently assigned episodes and personal output
          </p>
        </div>
        <button
          type="button"
          onClick={onClaim}
          disabled={claiming}
          className="inline-flex h-10 items-center gap-2 rounded-md bg-brand-orange px-4 text-sm font-semibold text-white transition hover:bg-brand-orange-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {claiming ? (
            <LoaderCircle className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          Claim next
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-brand-border bg-brand-border lg:grid-cols-4">
        {[
          ['Assigned Episodes', summary.assigned_episodes.toLocaleString()],
          ['Completed Episodes', summary.completed_episodes.toLocaleString()],
          ['Labeled Fail Segments', summary.labeled_segments.toLocaleString()],
          ['Output Duration', formatDuration(summary.output_duration_s)],
        ].map(([label, value]) => (
          <div key={label} className="min-w-0 bg-brand-surface px-4 py-4">
            <div className="text-xs uppercase text-brand-text-secondary">{label}</div>
            <div className="mt-1 font-mono text-xl font-semibold text-brand-text-primary">
              {value}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-md border border-brand-border bg-brand-surface p-1">
          {filters.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.value}
                type="button"
                onClick={() => onFilterChange(item.value)}
                aria-pressed={filter === item.value}
                className={`inline-flex h-8 items-center gap-2 rounded px-3 text-xs font-medium transition ${
                  filter === item.value
                    ? 'bg-brand-surface-highlight text-brand-text-primary'
                    : 'text-brand-text-secondary hover:text-brand-text-primary'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {item.label}
              </button>
            );
          })}
        </div>

        <div className="flex min-w-0 flex-1 flex-wrap justify-end gap-2">
          <form onSubmit={handleSearch} className="relative min-w-[240px] max-w-sm flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-text-secondary" />
            <input
              type="search"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Search Episode ID"
              className="h-10 w-full rounded-md border border-brand-border bg-brand-surface pl-9 pr-9 text-sm text-brand-text-primary outline-none focus:border-brand-orange"
            />
            {searchValue ? (
              <button
                type="button"
                onClick={() => {
                  setSearchValue('');
                  onSearch('');
                }}
                aria-label="Clear Episode search"
                className="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded text-brand-text-secondary hover:text-brand-text-primary"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </form>
          <select
            value={`${sort}:${order}`}
            onChange={(event) => {
              const selected = sortOptions.find(
                (option) => `${option.sort}:${option.order}` === event.target.value
              );
              if (selected) onSortChange(selected.sort, selected.order);
            }}
            aria-label="Sort My Tasks"
            className="h-10 rounded-md border border-brand-border bg-brand-surface px-3 text-sm text-brand-text-primary outline-none focus:border-brand-orange"
          >
            {sortOptions.map((option) => (
              <option
                key={`${option.sort}:${option.order}`}
                value={`${option.sort}:${option.order}`}
              >
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-5 overflow-hidden rounded-lg border border-brand-border bg-brand-surface">
        {loading ? (
          <div className="flex min-h-48 items-center justify-center gap-2 text-sm text-brand-text-secondary">
            <LoaderCircle className="h-4 w-4 animate-spin" /> Loading tasks
          </div>
        ) : visibleTasks.length === 0 ? (
          <div className="flex min-h-48 items-center justify-center text-sm text-brand-text-secondary">
            No matching {filters.find((item) => item.value === filter)?.label.toLowerCase()} tasks
          </div>
        ) : (
          <>
            <div className="max-w-full overflow-x-auto">
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead className="border-b border-brand-border bg-brand-surface-highlight/50 text-xs uppercase text-brand-text-secondary">
                  <tr>
                    <th className="px-4 py-3 font-medium">Episode</th>
                    <th className="px-4 py-3 font-medium">Progress</th>
                    <th className="px-4 py-3 font-medium">Fail duration</th>
                    <th className="px-4 py-3 font-medium">Assigned</th>
                    <th className="px-4 py-3 font-medium">Edit until</th>
                    <th className="px-4 py-3 font-medium">DDB sync</th>
                    <th className="w-16 px-4 py-3">
                      <span className="sr-only">Open</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleTasks.map((task) => (
                    <tr
                      key={task.episode_id}
                      className="border-b border-brand-border/70 last:border-b-0"
                    >
                      <td className="px-4 py-3 font-mono text-xs text-brand-text-primary">
                        {task.episode_id}
                      </td>
                      <td className="px-4 py-3 text-brand-text-secondary">
                        {task.completed_segment_count} / {task.fail_segment_count}
                      </td>
                      <td className="px-4 py-3 text-brand-text-secondary">
                        {formatDuration(task.output_duration_s ?? 0)}
                      </td>
                      <td className="px-4 py-3 text-brand-text-secondary">
                        {formatDate(task.assigned_at_ms)}
                      </td>
                      <td className="px-4 py-3 text-brand-text-secondary">
                        {formatDate(task.edit_until_ms)}
                      </td>
                      <td className="px-4 py-3 capitalize text-brand-text-secondary">
                        {task.ddb_sync_status}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => onOpen(task.episode_id)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-brand-text-secondary hover:bg-brand-surface-highlight hover:text-brand-text-primary"
                          aria-label={`Open ${task.episode_id}`}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasMore ? (
              <div className="border-t border-brand-border px-4 py-3 text-center">
                <button
                  type="button"
                  onClick={onLoadMore}
                  disabled={loadingMore}
                  className="inline-flex h-9 items-center gap-2 rounded-md border border-brand-border px-4 text-sm text-brand-text-primary disabled:opacity-50"
                >
                  {loadingMore ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                  Load more
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </main>
  );
}
